import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AudioLines, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

export default function Success() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [trialEnd, setTrialEnd] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const res = await api.get(`/stripe/checkout-status/${sessionId}`);
        const u = res.data.user;
        setUser(u);
        if (u.has_active_subscription) {
          setStatus("success");
          setTrialEnd(u.trial_end);
          return;
        }
      } catch (err) {
        // Polling will retry on next interval; only log if persistent
        if (pollsRef.current >= MAX_POLLS - 1) {
          console.warn("Stripe status poll failed:", err?.message);
        }
      }
      if (pollsRef.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate, setUser]);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 sm:px-8 py-10 relative">
      <div className="w-full max-w-md flex flex-col gap-6 relative z-10">
        <div className="flex items-center gap-2.5 justify-center mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
            <AudioLines className="w-5 h-5 text-[#FF2D55]" strokeWidth={2.2} />
          </div>
          <span className="font-display font-black text-white text-2xl tracking-tight">KeyFinder</span>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-8 shadow-2xl text-center">
          {status === "checking" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#0F0F13] border border-white/10 mx-auto flex items-center justify-center mb-5">
                <Loader2 className="w-6 h-6 text-[#FF2D55] animate-spin" />
              </div>
              <h1 className="font-display text-2xl font-black text-white tracking-tight">
                Confirming your subscription…
              </h1>
              <p className="text-sm text-zinc-500 mt-2 font-body">
                Just a moment while we verify payment with Stripe.
              </p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 mx-auto flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-[#39FF14]" strokeWidth={2.2} />
              </div>
              <h1 className="font-display text-3xl font-black text-white tracking-tight led-glow-green">
                You&apos;re in!
              </h1>
              <p className="text-sm text-zinc-400 mt-3 font-body leading-relaxed">
                Your 3-day free trial has started. Analyze as many songs as you like.
                {trialEnd && (
                  <>
                    <br />
                    <span className="text-zinc-500 text-xs">
                      Trial ends {new Date(trialEnd).toLocaleDateString()}.
                    </span>
                  </>
                )}
              </p>
              <button
                onClick={() => navigate("/", { replace: true })}
                data-testid="success-continue-button"
                className="mt-6 w-full bg-[#FF2D55] text-white hover:bg-[#D11B47] font-bold px-6 py-3.5 rounded-full text-sm font-display tracking-tight transition-all"
              >
                Start analyzing
              </button>
            </>
          )}
          {status === "timeout" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/30 mx-auto flex items-center justify-center mb-5">
                <AlertTriangle className="w-7 h-7 text-[#FFB800]" strokeWidth={2.2} />
              </div>
              <h1 className="font-display text-2xl font-black text-white tracking-tight">
                Still processing…
              </h1>
              <p className="text-sm text-zinc-400 mt-3 font-body leading-relaxed">
                Stripe is taking a bit longer than usual. Your subscription should appear in a few minutes — check back from the app.
              </p>
              <button
                onClick={() => navigate("/", { replace: true })}
                className="mt-6 w-full bg-white/5 hover:bg-white/10 text-white font-medium px-6 py-3.5 rounded-full text-sm font-display border border-white/5"
              >
                Back to app
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
